import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { InitialStateLoader } from './InitialStateLoader.js';
import { type InitialState } from './reducer/state.js';
import { usePayoutSetupWorkflow } from './usePayoutSetupWorkflow.js';
import { useMount } from '@contra/react-hooks/useMount';
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useMemo,
  useState,
} from 'react';

const PayoutSetupModals = lazy(async () => {
  return {
    default: (await import('./PayoutSetupModals.js')).PayoutSetupModals,
  };
});

type PayoutSetupContext = {
  cancel: ReturnType<typeof usePayoutSetupWorkflow>['actions']['cancel'];
  isActive: boolean;
  start: ReturnType<typeof usePayoutSetupWorkflow>['actions']['start'];
};

type Props = {
  readonly children: ReactNode;
  readonly initialState: InitialState;
};

const defaultFunction = () => {
  throw new Error('Must be called inside of Payout Setup Context Provider');
};

export const PayoutSetupContext = createContext<PayoutSetupContext>({
  cancel: defaultFunction,
  isActive: false,
  start: defaultFunction,
});

const PayoutSetupContextRealProvider = ({ children, initialState }: Props) => {
  const [urlSearchParameters] = useURLParameters('*');
  const refreshStripe = urlSearchParameters.refreshStripe;
  const fromStripeOnboardingSetupDebitCard = urlSearchParameters.debitCard;
  const paypalAuthenticationCode = urlSearchParameters.paypalCode;
  const showAddPayoutMethod = urlSearchParameters.addPayoutMethod;
  const fromPayoneerOnboarding = urlSearchParameters.fromPayoneerOnboarding;

  const { actions, state } = usePayoutSetupWorkflow(initialState);

  const value = useMemo<PayoutSetupContext>(
    () => ({
      cancel: actions.cancel,
      isActive: state.view !== null,
      start: actions.start,
    }),
    [actions.cancel, actions.start, state.view],
  );

  const location = useLocation();

  const navigation = useNavigation();

  useMount(() => {
    if (refreshStripe) {
      // clear the search param from the URL and
      // restart the payout setup flow
      const newUrl = removeSearchParameter(
        removeSearchParameter(location.path, 'refreshStripe'),
        'debitCard',
      );

      navigation.replace(newUrl);

      actions.refreshAfterRedirectFromStripe(
        Boolean(fromStripeOnboardingSetupDebitCard),
      );
    }

    if (showAddPayoutMethod) {
      const newUrl = removeSearchParameter(
        location.currentURL,
        'addPayoutMethod',
      );

      navigation.replace(newUrl);

      actions.start();
    }

    if (paypalAuthenticationCode) {
      navigation.push(location.pathname);

      actions.connectPaypal(paypalAuthenticationCode);
    }

    if (fromPayoneerOnboarding) {
      actions.syncPayoneer();
    }
  });

  return (
    <PayoutSetupContext.Provider value={value}>
      {children}

      <Suspense>
        <PayoutSetupModals
          actions={actions}
          state={state}
        />
      </Suspense>
    </PayoutSetupContext.Provider>
  );
};

export const PayoutSetupContextProvider = ({
  children,
}: Pick<Props, 'children'>) => {
  const currentUser = useCurrentUser();
  const { selectedUserType } = useSelectedUserType();
  const [contextInitialValue, setContextInitialValue] = useState<
    'NOT_AVAILABLE' | InitialState | null
  >(null);

  if (
    !currentUser?.emailAddress ||
    contextInitialValue === 'NOT_AVAILABLE' ||
    selectedUserType === 'CLIENT'
  ) {
    // anonymous users have no use for the context
    return <>{children}</>;
  }

  // If the context value hasn't been set, use the InitialStateLoader
  // component to fetch it and set it
  // This removes the need for the consumer of this context
  // to fetch and provide the required initial state
  if (contextInitialValue === null) {
    return (
      <Suspense fallback={null}>
        <InitialStateLoader setValue={setContextInitialValue} />
      </Suspense>
    );
  }

  return (
    <PayoutSetupContextRealProvider initialState={contextInitialValue}>
      {children}
    </PayoutSetupContextRealProvider>
  );
};
